import { baseApi, reportingApi, pulseCheck, directFeedbackApi, notesApi } from '~src/services/ApiController';
import {
  GET_USER_ANSWERS_SUCCESS,
  GET_USER_BY_ID_SUCCESS,
  GET_USER_QUESTIONS_IMPROVE_SUCCESS,
  GET_USER_NEWS_SUCCESS,
  GET_USER_QUESTIONS_SUCCESS,
  GET_USER_TOPICS_SUCCESS,
  USER_PROFILE_ANY_FAILED,
  USER_PROFILE_ANY_REQUEST,
  DeleteUserSurvey,
  CopyUserSurvey,
  GetUserSurveys,
  GetUserTeam,
  GetDirectFeedback,
} from '~src/modules/reports/userProfile/constants/actions';
import { AsyncAction } from '~src/services/apiMiddleware';
import { User } from '~src/interfaces/user.interface';
import { TopicsResponse } from '~src/modules/reports/interfaces/topics.interface';
import { QuestionResponse } from '~src/modules/reports/interfaces/questions.interface';
import { ImproveResponse } from '~src/modules/reports/interfaces/improve.interface';
import { AnswerResponse } from '~src/modules/reports/interfaces/answer.interface';
import { NewsResponse } from '~src/modules/reports/interfaces/news.interface';
import {
  ManagerTeamRespI,
  UserSurveysRespI,
} from '~src/modules/reports/userProfile/reducers/userProfileReducer';
import { Survey } from '~src/modules/surveys/interfaces';
import { getUrlFromObject } from '~src/utils';
import { NotesResponse } from '~src/modules/reports/interfaces/notes.interface';
import { GetNote, GetSubjectNotes } from '~src/modules/reports/shared/components/notes/constants/actions';

import { SortingConfUserSurveys } from './userProfileActions.interface';

export const fetchUserById = (id: string): AsyncAction<User, undefined> => ({
  types: [USER_PROFILE_ANY_REQUEST, GET_USER_BY_ID_SUCCESS, USER_PROFILE_ANY_REQUEST],
  callAPI: () => baseApi.get(`/users/${id}?manager_attributes=true`),
});

export const fetchUserTopicsList = (queryStr: string, userId: string): AsyncAction<TopicsResponse, undefined> => ({
  types: [USER_PROFILE_ANY_REQUEST, GET_USER_TOPICS_SUCCESS, USER_PROFILE_ANY_FAILED],
  callAPI: () => reportingApi.get(`/reports/user/${userId}/topics${queryStr}`),
});

export const fetchUserQuestionsList = (
  topicId: string,
  userId: string,
  params: string,
): AsyncAction<QuestionResponse, undefined> => ({
  types: [USER_PROFILE_ANY_REQUEST, GET_USER_QUESTIONS_SUCCESS, USER_PROFILE_ANY_FAILED],
  callAPI: () => reportingApi.get(`/reports/user/${userId}/topic/${topicId}/questions${params}`),
});

export const fetchUserQuestionsImproveList = (
  topicId: string,
  userId: string,
  params: string,
): AsyncAction<ImproveResponse, undefined> => ({
  types: [USER_PROFILE_ANY_REQUEST, GET_USER_QUESTIONS_IMPROVE_SUCCESS, USER_PROFILE_ANY_FAILED],
  callAPI: () => reportingApi.get(`/reports/user/${userId}/topic/${topicId}/how-to-improve${params}`),
});

export const fetchUserAnswersList = (
  questionId: string,
  userId: string,
  queryStr: string,
): AsyncAction<AnswerResponse, undefined> => ({
  types: [USER_PROFILE_ANY_REQUEST, GET_USER_ANSWERS_SUCCESS, USER_PROFILE_ANY_FAILED],
  callAPI: () => reportingApi.get(`/reports/user/${userId}/question/${questionId}/answers${queryStr}`),
});

export const fetchUserNewsList = (userId: string, params?: string): AsyncAction<NewsResponse, undefined> => ({
  types: [USER_PROFILE_ANY_REQUEST, GET_USER_NEWS_SUCCESS, USER_PROFILE_ANY_REQUEST],
  callAPI: () => reportingApi.get(`/reports/user/${userId}/news?${params}`),
});

export const fetchManagerTeam = (
  id: string,
  sorting: { [key: string]: string | null },
): AsyncAction<ManagerTeamRespI, undefined> => {
  const queryStr = getUrlFromObject(sorting);
  return {
    types: GetUserTeam,
    callAPI: () => reportingApi.get(`/reports/user/${id}/direct-reports${queryStr}`),
  };
};

export const fetchDirectFeedbackList = (sorting: {
  [key: string]: string | null;
}): AsyncAction<ManagerTeamRespI, undefined> => {
  const queryStr = getUrlFromObject(sorting);
  return {
    types: GetDirectFeedback,
    callAPI: () => directFeedbackApi.get(`/api/feedbacks${queryStr}`),
  };
};

export const fetchUserSurveysList = (
  userId: string,
  params: SortingConfUserSurveys = {},
): AsyncAction<UserSurveysRespI, undefined> => {
  const tmpParams: Partial<SortingConfUserSurveys> & { filterManagerRequest?: 'true' } = { ...params };
  if (tmpParams.filterScope && tmpParams.filterScope === 'user_manager') {
    delete tmpParams.filterScope;
    tmpParams.filterManagerRequest = 'true';
  }
  let queryParams = getUrlFromObject(tmpParams);
  queryParams += queryParams.length > 1 ? '&' : '';
  queryParams += 'include=segments';
  return {
    types: [GetUserSurveys.Request, GetUserSurveys.Success, GetUserSurveys.Failed],
    callAPI: () => reportingApi.get(`/reports/user/${userId}/surveys${queryParams}`),
  };
};

export const copySurvey = (id: string): AsyncAction<Survey, undefined> => ({
  types: CopyUserSurvey,
  callAPI: () => pulseCheck.post(`/surveys/${id}/copy`),
});

export const deleteSurvey = (id: string): AsyncAction<unknown, undefined> => ({
  types: DeleteUserSurvey,
  callAPI: () => pulseCheck.delete(`/surveys/${id}`),
  payload: { id },
});

export const fetchNoteListForSubject = (
  subjectId: string,
  sorting: { [key: string]: string | null },
  includes?: string[],
): AsyncAction<NotesResponse, undefined> => ({
  types: GetSubjectNotes,
  callAPI: () =>
    notesApi.get(
      `/notes${getUrlFromObject(sorting)}` +
        `&filter.subject=${subjectId}` +
        `${includes && includes.length > 0 ? '&include=' + includes.join(',') : ''}`,
    ),
});

export const fetchNote = (noteId: string, includes: string[]): AsyncAction<NotesResponse, undefined> => ({
  types: GetNote,
  callAPI: () => notesApi.get(`/notes/${noteId}?include=${includes.join(',')}`),
});

export const updateNoteVisibilities = (noteId: string, segmentIds: string[]): AsyncAction<unknown, undefined> => ({
  types: GetNote,
  callAPI: () => notesApi.post(`/notes/${noteId}/visibility`, segmentIds),
});
